import {
  FileText,
  Calendar,
  Building2,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Clock,
  Send,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type {
  Invoice, PaymentStatus 
} from '@/types/invoices';
import {
  INVOICE_STATUS_LABELS, INVOICE_STATUS_COLORS, PAYMENT_STATUS_LABELS 
} from '@/types/invoices';

function PaymentStatusIcon({ status }: { readonly status: PaymentStatus }) {
  switch (status) {
    case 'paid':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'partial':
      return <Clock className="h-4 w-4 text-amber-500" />;
    default:
      return <div className="h-4 w-4 rounded-full border border-muted-foreground" />;
  }
}

interface InvoiceCardProps {
  readonly invoice: Invoice;
  readonly onSubmit?: (invoiceId: string) => void;
  readonly onViewDetails?: (invoiceId: string) => void;
}

export function InvoiceCard({
  invoice, onSubmit, onViewDetails 
}: InvoiceCardProps) {
  const statusColor = INVOICE_STATUS_COLORS[invoice.status];
  const isOverdue = new Date(invoice.dueDate) < new Date() && invoice.status !== 'paid';
  const canSubmit = invoice.status === 'draft';

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base font-medium">{invoice.invoiceNumber}</CardTitle>
          </div>
          <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusColor}`}>
            {INVOICE_STATUS_LABELS[invoice.status]}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">To: {invoice.buyerName}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {invoice.poNumber && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Building2 className="h-4 w-4" />
            <span>PO: {invoice.poNumber}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>Invoice Date: {new Date(invoice.invoiceDate).toLocaleDateString()}</span>
        </div>

        <div className={`flex items-center gap-2 text-sm ${isOverdue ? 'text-red-600' : 'text-muted-foreground'}`}>
          {isOverdue ? <AlertTriangle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
          <span>Due: {new Date(invoice.dueDate).toLocaleDateString()}</span>
          {isOverdue && <span className="text-xs font-medium">(Overdue)</span>}
        </div>

        {invoice.paymentStatus && (
          <div className="flex items-center gap-2 text-sm">
            <PaymentStatusIcon status={invoice.paymentStatus} />
            <span className="text-muted-foreground">
              Payment: {PAYMENT_STATUS_LABELS[invoice.paymentStatus]}
            </span>
          </div>
        )}

        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground mb-2">Line Items ({invoice.lines.length}):</p>
          <div className="space-y-1">
            {invoice.lines.slice(0, 3).map((line) => (
              <div key={line.lineId} className="flex items-center justify-between text-sm">
                <span className="truncate flex-1">{line.itemName}</span>
                <span className="text-muted-foreground ml-2">${line.totalPrice.toFixed(2)}</span>
              </div>
            ))}
            {invoice.lines.length > 3 && (
              <p className="text-xs text-muted-foreground">+{invoice.lines.length - 3} more items</p>
            )}
          </div>
        </div>

        {invoice.notes && (
          <p className="text-sm text-muted-foreground italic border-l-2 border-muted pl-2">
            {invoice.notes}
          </p>
        )}

        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-1 text-primary">
            <DollarSign className="h-4 w-4" />
            <span className="text-lg font-bold">{invoice.total.toLocaleString()}</span>
            <span className="text-xs text-muted-foreground">{invoice.currency}</span>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          {canSubmit && onSubmit && (
            <Button size="sm" onClick={() => onSubmit(invoice.invoiceId)}>
              <Send className="h-4 w-4 mr-1" />
              Submit
            </Button>
          )}
          {onViewDetails && (
            <Button
              size="sm"
              variant={canSubmit ? 'outline' : 'default'}
              onClick={() => onViewDetails(invoice.invoiceId)}
            >
              View Details
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
